/**
 * outerQuerySources — the enclosing queries' tables a drilled subquery may
 * reference (a correlated `WHERE o2.x = o.x`). The column pickers list them
 * next to the subquery's own tables, so a correlated condition stays one pick
 * away without a "Show all tables" list that would also offer every unrelated
 * table.
 *
 * Only a subquery context has an outer query: a drilled subquery body is a
 * synthetic `<statementId>.sub.<start>-<end>` statement whose resolver scope is
 * a `subquery` frame. Which tables each enclosing level lets the body see —
 * its own UNION branch, the FROM items before a LATERAL / APPLY, the join
 * tree of an ON, none of a derived table's siblings, none of a WITH
 * statement's own tables from a CTE body — is the resolver's
 * `enclosingScopes` (each level read from its own text only, so a deep drill
 * reads the statement about once). An UPDATE / DELETE level also offers the
 * tables of its relation lists (`listedDmlSources`: MySQL `UPDATE a JOIN b`
 * keeps `b`) — from inside one of their JOIN ONs, only those the ON sees
 * (`EnclosingScope.listed`).
 *
 * A name the body can see at an inner level hides every enclosing source
 * bound under the same name (`bindingKeyOf`: the engine's identifier
 * comparison) — `o."status"` inside `FROM items o` reads `items`, so the outer
 * `orders o` is not offered. A name it cannot see there (a sibling after a
 * LATERAL, a derived table's sibling) hides nothing.
 */

import type { ParseOptions, SqlEngine } from '../../../../sql-grammar/model';
import { enclosingScopes, type EnclosingScope } from '../../../../sql-resolver/scope/enclosingScopes';
import { statementSpanOf, type VqeEditContext } from '../../edit/editContext';
import { querySourceOf, type QuerySource } from '../../../../sql-resolver/scope/querySources';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { SchemaSnapshot } from '../../../../sql-resolver/types/schema';
import { listedDmlSources, statementSources } from './dmlJoinSources';

/**
 * The sources a statement's pickers and pills work with: its own (+ the
 * UPDATE / DELETE relation-list ones its structure doesn't carry) and, in a
 * drilled subquery, the enclosing ones it can reference (`outerQuerySources`).
 */
export function scopeSources(
  editContext: VqeEditContext,
  structure: QueryStructure | null,
  snapshot?: SchemaSnapshot | null,
): { own: QuerySource[]; outer: QuerySource[] } {
  const engine = editContext.service.engineId;
  const own = statementSources(structure ? statementSpanOf(editContext) : null, structure, engine, snapshot);
  return { own, outer: outerQuerySources(editContext, engine, own) };
}

/**
 * `own` — the drilled statement's own sources: the names they bind hide the
 * enclosing queries' sources of the same name.
 */
export function outerQuerySources(
  editContext: VqeEditContext,
  engine: SqlEngine,
  own: readonly QuerySource[],
): QuerySource[] {
  const info = editContext.statementInfo;
  if (!info || editContext.scope?.scopeKind !== 'subquery') return [];
  const marker = info.id.indexOf('.sub.');
  const buffer = editContext.service.getParsedBuffer(editContext.documentId);
  if (marker < 0 || !buffer) return [];

  const scopes = enclosingScopes(buffer, info.id.slice(0, marker), info.range.startOffset, info.range.endOffset);
  const options = buffer.parsedDocument.options;
  const bound = new Set(own.flatMap((s) => (s.bindingKey !== undefined ? [s.bindingKey] : [])));
  const out: QuerySource[] = [];
  for (const scope of scopes) {
    const visible = visibleSources(scope, engine, options);
    for (const source of visible) {
      if (source.bindingKey === undefined || !bound.has(source.bindingKey)) out.push({ ...source, outer: true });
    }
    // Only a name the body can see hides the same name further out.
    for (const source of visible) {
      if (source.bindingKey !== undefined) bound.add(source.bindingKey);
    }
  }
  return out;
}

/** What the subquery below sees at `scope`'s level. */
function visibleSources(scope: EnclosingScope, engine: SqlEngine, options: ParseOptions): QuerySource[] {
  const span = { source: scope.text, start: 0, end: scope.text.length, options };
  if (scope.sees === 'all') return statementSources(span, scope.query, engine);
  const seen = scope.sees.map((item) => querySourceOf(item, engine));
  const only = scope.listed;
  if (!only) return seen;
  return [...seen, ...listedDmlSources(span, scope.query, engine, seen, (rel) => only.has(rel.start))];
}
